import type { IData, IDataPoint, TDescriptionTable } from 'Components/sq-graphics/interfaces';
import { uhdrsFunctionalAssessment, uhdrsFunctionalCapacity, uhdrsMotorRatingScale } from 'neuro-schemas';
import { isNumber, isPartialDate } from 'neuro-utils';

const { calculateScore: uhdrsMotorScore } = uhdrsMotorRatingScale;
const { calculateScore: countFunctionalAssessmentScore } = uhdrsFunctionalAssessment;
const { calculateScore: countFunctionalCapacityScore } = uhdrsFunctionalCapacity;

export const convertUhdrsIndependenceScaleToGraph = (
  docs: IDocumentWithTypeV2<'uhdrsIndependenceScale'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    d.value &&
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.value,
        id: 'uhdrsIndependence',
        title: d.value,
      });
  });
  return [
    {
      dataPoints,
      id: 'uhdrsIndependence',
      type: 'lineGraph',
      legend: fm('functionalPerformance.uhdrsIndependenceScale'),
    },
  ];
};

export const convertUhdrsFunctionalAssessmentToGraph = (
  docs: IDocumentWithTypeV2<'uhdrsFunctionalAssessment'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = countFunctionalAssessmentScore(d);
    if (isNumber(score)) {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: score,
        title: String(score),
        id: 'uhdrsFA',
      });
    }
  });
  return [
    {
      dataPoints,
      id: 'uhdrsFA',
      type: 'lineGraph',
      legend: fm('functionalPerformance.uhdrsFunctionalAssessment'),
    },
  ];
};

export const convertUhdrsFunctionalCapacityToGraph = (
  docs: IDocumentWithTypeV2<'uhdrsFunctionalCapacity'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = countFunctionalCapacityScore(d);
    if (isNumber(score)) {
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: score,
        title: String(score),
        id: 'uhdrsFC',
      });
    }
  });
  return [
    {
      dataPoints,
      type: 'lineGraph',
      legend: fm('functionalPerformance.uhdrsFunctionalCapacity'),
      id: 'uhdrsFC',
    },
  ];
};

export const convertUhdrsMotorRatingScaleToGraph1 = (
  docs: IDocumentWithTypeV2<'uhdrsMotorRatingScale'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const score = uhdrsMotorScore(d);
    if (!isNumber(score)) return;
    const descriptionManual: TDescriptionTable = [
      {
        title: fm('motorFunctionHuntington.uhdrsTotalScoreOnly'),
        values: d.totalScoreOnly?.[0] === true ? fm('general.yes') : fm('general.no'),
      },
      {
        title: fm('motorFunctionHuntington.uhdrsScore'),
        values: score,
      },
    ];
    const descriptionFilled: TDescriptionTable = [
      {
        title: fm('motorFunctionHuntington.uhdrsScore'),
        values: `${score}`,
      },
      {
        title: fm('motorFunctionHuntington.uhdrsMotorDysarthria'),
        values: d.dysarthria,
      },
      {
        title: fm('motorFunctionHuntington.uhdrsMotorRetropulsionPullTest'),
        values: d.retropulsionPullTest,
      },
      {
        title: fm('motorFunctionHuntington.uhdrsMotorGait'),
        values: d.gait,
      },
      {
        title: fm('motorFunctionHuntington.uhdrsMotorTandemWalking'),
        values: d.tandemWalking,
      },
    ];
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: score,
      id: 'uhdrsMotorRatingScale1',
      title: score.toString(),
      description:
        d.totalScoreOnly?.[0] === true && d.manualScore
          ? descriptionManual
          : !d.totalScoreOnly?.[0]
            ? descriptionFilled
            : undefined,
    });
  });
  return [
    {
      dataPoints,
      type: 'lineGraph',
      legend: 'UHDRS Motor I',
      id: 'uhdrsMotor1',
    },
  ];
};

export const convertUhdrsMotorRatingScaleToGraph2 = (
  docs: IDocumentWithTypeV2<'uhdrsMotorRatingScale'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsDysa: IDataPoint[] = [];
  const dataPointsRetropulsion: IDataPoint[] = [];
  const dataPointsGait: IDataPoint[] = [];
  const dataPointsTandem: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    if (d.dysarthria || d.dysarthria === 0) {
      dataPointsDysa.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.dysarthria.toString(),
        id: 'dysarthria',
        title: d.dysarthria.toString(),
      });
    }
    if (d.retropulsionPullTest || d.retropulsionPullTest === 0) {
      dataPointsRetropulsion.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.retropulsionPullTest.toString(),
        title: d.retropulsionPullTest.toString(),
        id: 'retropulsionPullTest',
      });
    }
    if (d.gait || d.gait === 0) {
      dataPointsGait.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.gait.toString(),
        id: 'gait',
        title: d.gait.toString(),
      });
    }
    if (d.tandemWalking || d.tandemWalking === 0) {
      dataPointsTandem.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.tandemWalking.toString(),
        id: 'tandemWalking',
        title: d.tandemWalking.toString(),
      });
    }
  });
  return [
    {
      dataPoints: dataPointsDysa,
      type: 'lineGraph',
      id: 'dysarthria',
      legend: fm('motorFunctionHuntington.uhdrsMotorDysarthria'),
    },
    {
      dataPoints: dataPointsRetropulsion,
      type: 'lineGraph',
      id: 'retropulsionPullTest',
      legend: fm('motorFunctionHuntington.uhdrsMotorRetropulsionPullTest'),
    },
    {
      dataPoints: dataPointsGait,
      type: 'lineGraph',
      id: 'gait',
      legend: fm('motorFunctionHuntington.uhdrsMotorGait'),
    },
    {
      dataPoints: dataPointsTandem,
      type: 'lineGraph',
      id: 'tandemWalking',
      legend: fm('motorFunctionHuntington.uhdrsMotorTandemWalking'),
    },
  ];
};

export const convertUhdrsConfidenceIndexToGraph = (
  docs: IDocumentWithTypeV2<'uhdrsConfidenceIndex'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date) && !d.index) return;
    let value: string | undefined = undefined;
    switch (d.index) {
      case '0': {
        value = 'Normal';
        break;
      }
      case '1': {
        value = '<50%';
        break;
      }
      case '2': {
        value = '50-89%';
        break;
      }
      case '3': {
        value = '90-98%';
        break;
      }
      case '4': {
        value = '≥99%';
        break;
      }
    }
    d.date &&
      value &&
      dataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value,
        title: fm(`diagnosis.huntington.uhdrs.form.opts.${d.index}`),
        id: 'uhdrsDCL',
      });
  });
  return [
    {
      dataPoints,
      type: 'lineGraph',
      legend: fm('diagnosis.huntington.uhdrs.title'),
      id: 'uhdrsDiagnosticConfidenceLevel',
    },
  ];
};

const pbaSFields = [
  'depressedMood',
  'suicidalIdeation',
  'anxiety',
  'irritability',
  'angryOrAggressiveBehaviour',
  'apathy',
  'perseveration',
  'obsessiveCompulsiveBehaviours',
  'delusionsOrParanoidThinking',
  'hallucinations',
  'disorientedBehaviour',
];

const isPbaSScoreValid = (score: unknown): boolean =>
  (typeof score === 'number' || typeof score === 'string') &&
  (score === 0 ||
    score === 1 ||
    score === 2 ||
    score === 3 ||
    score === 4 ||
    score === 8 ||
    score === 9 ||
    score === 'U' ||
    score === 'N');

// If score is 8, 9, U or N, score for pbas field is not calculated
const isPbaSScoreAcceptable = (score: any): boolean =>
  (typeof score === 'number' || typeof score === 'string') && ![8, 9, 'U', 'N'].includes(score);

export const convertPbasToGraph = (
  docs: IDocumentWithTypeV2<'pbaS'>[],
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];

  docs.forEach(doc => {
    pbaSFields.forEach(field => {
      const severity = doc[`${field}Severity` as keyof typeof doc];
      const frequency = doc[`${field}Frequency` as keyof typeof doc];
      const worst = doc[`${field}Worst` as keyof typeof doc];

      const isSeverityValid = isPbaSScoreValid(severity);
      const isFrequencyValid = isPbaSScoreValid(frequency);
      const isSeverityAcceptable = isPbaSScoreAcceptable(severity);
      const isFrequencyAcceptable = isPbaSScoreAcceptable(frequency);

      let additionalValue = '-';

      if (isSeverityValid && isFrequencyValid) {
        additionalValue =
          isSeverityAcceptable && isFrequencyAcceptable ? (Number(severity) * Number(frequency)).toString() : 'N/A';
      }

      const description: TDescriptionTable = [
        {
          title: `${fm(`behaviour.labels.pbaS.${field}Severity`)}: `,
          values: severity != null ? severity.toString() : '-',
        },
        {
          title: `${fm(`behaviour.labels.pbaS.${field}Frequency`)}: `,
          values: frequency != null ? frequency.toString() : '-',
        },
        {
          title: `${fm(`behaviour.labels.pbaS.${field}Worst`)}: `,
          values: worst != null ? worst.toString() : '-',
        },
      ];

      if (doc.date) {
        dataPoints.push({
          date: dateFromPartialUpdateTimeframe(doc.date),
          id: field,
          value: fm(`behaviour.labels.pbaS.${field}`),
          additionalValue,
          description,
          title: additionalValue,
        });
      }
    });
  });
  return [{ dataPoints, id: 'pbaS', legend: 'PBA-s', type: 'heatGraph' }];
};
